import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Shield } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) { setError("Reset token missing from link."); return; }
    if (pw !== confirm) { setError("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => nav("/login"), 1500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-950 text-slate-100">
      <form onSubmit={submit} className="w-full max-w-md" data-testid="reset-password-form">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-10 w-10 grid place-items-center rounded-md bg-blue-600/20 border border-blue-500/40">
            <Shield className="h-5 w-5 text-blue-400" />
          </div>
          <div className="font-heading text-xl font-semibold">Crew Ready</div>
        </div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight mb-2">Set a new password</h1>
        <p className="text-sm text-slate-400 mb-6">Choose a new password for your account.</p>
        {done ? (
          <div className="rag-green px-4 py-3 rounded-md text-sm" data-testid="reset-success">Password updated. Redirecting to sign in…</div>
        ) : (
          <>
            <label className="block text-xs uppercase tracking-widest text-slate-400 font-mono-num mb-1.5">New password</label>
            <PasswordInput testId="reset-new-password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-md bg-slate-900 border border-slate-800 focus:border-blue-500 outline-none text-sm" />
            <label className="block text-xs uppercase tracking-widest text-slate-400 font-mono-num mt-4 mb-1.5">Confirm new password</label>
            <PasswordInput testId="reset-confirm-password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-md bg-slate-900 border border-slate-800 focus:border-blue-500 outline-none text-sm" />
            {error && <div data-testid="reset-error" className="mt-4 text-sm rag-red px-3 py-2 rounded">{error}</div>}
            <button type="submit" disabled={busy} data-testid="reset-submit"
              className="mt-6 w-full py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white font-medium transition-colors disabled:opacity-60">
              {busy ? "Saving…" : "Set new password"}
            </button>
          </>
        )}
        <div className="mt-6 text-sm text-slate-400 text-center">
          <Link to="/login" className="text-blue-400 hover:text-blue-300">Back to sign in</Link>
        </div>
      </form>
    </div>
  );
}
